import { useState, useRef, useEffect, useCallback } from 'react';
import { Save, X, CheckCircle, AlertCircle, Loader2, Blocks, RotateCcw } from 'lucide-react';
import { supabase } from '../supabase/client';

// Scratch 编辑器 iframe 路径（同域子路径部署，与 ScratchEditorPage 一致）
const SCRATCH_EDITOR_URL = '/scratch-editor/index.html';

interface ScratchCodingAnswerAreaProps {
  questionId: string;
  paperId?: string;
  paperTitle?: string;
  questionTitle?: string;
  userId?: string;
  isSubmitted?: boolean;
  /** 已有答案（.sb3 文件 URL，与上传模式格式一致） */
  answer?: string;
  onAnswerChange: (answer: string) => void;
}

/**
 * Scratch 图形化编程题在线作答组件（answerMode === 'online' 时使用）
 *
 * 交互流程：
 * 1. 答题区显示「打开编辑器在线答题」入口按钮（不直接内嵌编辑器）
 * 2. 点击后弹出全屏 Scratch 编辑器（覆盖整个页面）
 * 3. 学员在全屏编辑器中完成作品，点击「保存作答」：
 *    postMessage GET_SB3_DATA 获取 .sb3 Blob → 上传 coding-answers 桶 → 答案记为文件 URL
 * 4. 关闭编辑器回到答题区，显示已保存状态 + 「继续编辑」入口
 *
 * 答案格式与上传模式完全一致（coding-answers 桶的 .sb3 公共 URL），
 * ScratchResultDetail / 教师阅卷无需区分来源
 */
export default function ScratchCodingAnswerArea({
  questionId,
  paperId,
  paperTitle,
  questionTitle,
  userId,
  isSubmitted = false,
  answer,
  onAnswerChange,
}: ScratchCodingAnswerAreaProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pendingCallbacks = useRef<Map<string, (data: any) => void>>(new Map());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [savedThisSession, setSavedThisSession] = useState(false);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 每次打开编辑器时自动导入一次已有作答（保存产生的新答案不重复导入）
  const autoLoadUrlRef = useRef<string | null>(null);
  const autoLoadedRef = useRef(false);

  // 当前有效答案：父组件传入的 answer 或本次会话刚保存的 URL
  const effectiveAnswer = answer || savedUrl || undefined;

  // 监听编辑器回传消息（SB3_DATA_RESPONSE / PROJECT_LOADED）
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = event.data;
      if (!message?.type) return;
      if (message.callbackId && pendingCallbacks.current.has(message.callbackId)) {
        pendingCallbacks.current.get(message.callbackId)?.(message);
        pendingCallbacks.current.delete(message.callbackId);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // 全屏编辑器打开期间锁定背景滚动
  useEffect(() => {
    if (editorOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [editorOpen]);

  // 带回调的消息发送（请求-响应模式）
  const sendToEditorWithCallback = useCallback((message: Record<string, unknown>): Promise<any> => {
    return new Promise((resolve, reject) => {
      const callbackId = `cb_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      // saveProjectSb3 / loadProject 可能较慢（编辑器内 VM 序列化/反序列化），给足超时
      const timeout = setTimeout(() => {
        pendingCallbacks.current.delete(callbackId);
        reject(new Error('编辑器响应超时，请重试'));
      }, 60000);
      pendingCallbacks.current.set(callbackId, (data) => {
        clearTimeout(timeout);
        resolve(data);
      });
      iframeRef.current?.contentWindow?.postMessage({ ...message, callbackId }, window.location.origin);
    });
  }, []);

  // 导入 .sb3 到编辑器（学员可继续编辑已保存的作答）
  const loadProjectFromUrl = useCallback(async (sb3Url: string) => {
    setLoadingProject(true);
    setError(null);
    try {
      const res = await fetch(sb3Url);
      if (!res.ok) throw new Error(`下载已保存作答失败 (HTTP ${res.status})`);
      const arrayBuffer = await res.arrayBuffer();
      const response = await sendToEditorWithCallback({ type: 'LOAD_PROJECT', sb3Data: arrayBuffer });
      if (response?.success === false) {
        throw new Error(response?.error || '项目加载失败');
      }
    } catch (err: any) {
      setError(err?.message || '导入失败，请重试');
    } finally {
      setLoadingProject(false);
    }
  }, [sendToEditorWithCallback]);

  // 编辑器就绪后自动导入已有作答（每次打开仅一次，避免覆盖学员当前编辑内容）
  useEffect(() => {
    if (editorOpen && editorReady && !autoLoadedRef.current && autoLoadUrlRef.current) {
      autoLoadedRef.current = true;
      loadProjectFromUrl(autoLoadUrlRef.current);
    }
  }, [editorOpen, editorReady, loadProjectFromUrl]);

  // 打开全屏编辑器
  const openEditor = () => {
    setError(null);
    setSavedThisSession(false);
    setEditorReady(false);
    autoLoadedRef.current = false;
    autoLoadUrlRef.current = effectiveAnswer || null;
    setEditorOpen(true);
  };

  // 关闭全屏编辑器（本次打开未保存过时提醒）
  const closeEditor = () => {
    if (!savedThisSession) {
      const confirmed = window.confirm('当前作品尚未保存，关闭后未保存的修改将丢失。确定要关闭编辑器吗？');
      if (!confirmed) return;
    }
    setEditorOpen(false);
  };

  // 保存作答：从编辑器获取 .sb3 → 上传 coding-answers → 记录答案 URL
  const handleSave = async () => {
    if (saving || !editorReady) return;
    setSaving(true);
    setError(null);
    try {
      const response = await sendToEditorWithCallback({ type: 'GET_SB3_DATA' });
      if (response?.error || !response?.sb3Blob) {
        throw new Error(response?.error || '无法获取项目数据');
      }
      const blob = response.sb3Blob as Blob;

      // 上传路径格式与 CodingFileUpload 保持一致（ScratchResultDetail / 教师阅卷兼容）
      const timestamp = Date.now();
      const safePaperTitle = (paperTitle || paperId || 'custom')
        .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '_')
        .substring(0, 50);
      const safeQuestionTitle = (questionTitle || questionId || 'question')
        .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '_')
        .substring(0, 50);
      const filePath = `${safePaperTitle}/${safeQuestionTitle}/${userId || 'anonymous'}/${timestamp}_scratch_answer.sb3`;

      // 使用 ArrayBuffer 上传（与 CodingFileUpload 一致，直接传 File/Blob 会 400）
      const arrayBuffer = await blob.arrayBuffer();
      const { error: uploadError } = await supabase.storage
        .from('coding-answers')
        .upload(filePath, arrayBuffer, {
          contentType: 'application/octet-stream',
          upsert: true,
        });
      if (uploadError) throw new Error(uploadError.message);

      const { data: urlData } = supabase.storage.from('coding-answers').getPublicUrl(filePath);
      setSavedUrl(urlData.publicUrl);
      setSavedThisSession(true);
      onAnswerChange(urlData.publicUrl);
    } catch (err: any) {
      setError(err?.message || '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // ============ 答题区（入口 / 已保存状态） ============
  const renderAnswerArea = () => {
    // 已提交/已揭示：只读展示
    if (isSubmitted) {
      return (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">已提交 Scratch 作品</p>
            <p className="text-xs text-green-600">答案已随试卷提交，可在成绩页查看详情</p>
          </div>
        </div>
      );
    }

    // 已保存作答：状态卡片 + 继续编辑入口
    if (effectiveAnswer) {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800">
                {savedThisSession ? '作答已保存' : '已保存作答'}
              </p>
              <p className="text-xs text-green-600">
                {savedThisSession
                  ? '提交试卷时将使用此作品，可继续编辑后重新保存'
                  : '可重新打开编辑器继续编辑，提交试卷时使用最新保存的作品'}
              </p>
            </div>
          </div>
          <button
            onClick={openEditor}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-sm text-emerald-700 border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            重新打开编辑器继续编辑
          </button>
        </div>
      );
    }

    // 未作答：入口卡片
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-3 bg-blue-50 rounded-xl flex items-center justify-center">
          <Blocks className="w-6 h-6 text-blue-600" />
        </div>
        <p className="text-sm font-medium text-gray-700 mb-1">Scratch 图形化编程题</p>
        <p className="text-xs text-gray-500 mb-4">
          点击下方按钮打开全屏 Scratch 编辑器，拖拽积木完成作品后保存作答
        </p>
        <button
          onClick={openEditor}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Blocks className="w-4 h-4" />
          打开编辑器在线答题
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {renderAnswerArea()}

      {error && !editorOpen && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <p className="text-xs text-gray-500">
        在全屏 Scratch 编辑器中完成作品并点击「保存作答」后，答案将随试卷提交自动保存。手机端屏幕较小，建议使用电脑端作答。
      </p>

      {/* ============ 全屏 Scratch 编辑器弹层 ============ */}
      {editorOpen && (
        <div className="fixed inset-0 z-[100] bg-gray-900 flex flex-col">
          {/* 顶部工具栏 */}
          <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                <Blocks className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {questionTitle || 'Scratch 在线编程'}
                </p>
                <p className="text-xs text-gray-500 hidden sm:block">
                  {savedThisSession ? '作品已保存，可继续编辑后重新保存' : '完成作品后请点击「保存作答」'}
                </p>
              </div>
              {savedThisSession && (
                <span className="hidden sm:flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full text-xs text-green-700 font-medium shrink-0">
                  <CheckCircle className="w-3 h-3" />
                  已保存
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleSave}
                disabled={saving || !editorReady}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  saving || !editorReady
                    ? 'bg-gray-300 text-white cursor-not-allowed'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                }`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? '保存中...' : savedThisSession ? '重新保存' : '保存作答'}
              </button>
              <button
                onClick={closeEditor}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm text-gray-600 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">关闭编辑器</span>
              </button>
            </div>
          </div>

          {/* 编辑器主体 */}
          <div className="flex-1 relative bg-white">
            {(!editorReady || loadingProject) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">
                    {loadingProject ? '正在导入已保存的作答...' : '正在加载 Scratch 编辑器...'}
                  </p>
                  <p className="text-gray-400 text-sm mt-2">首次加载可能需要几秒钟</p>
                </div>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={SCRATCH_EDITOR_URL}
              className="absolute inset-0 w-full h-full border-0"
              title="Scratch 在线编程"
              onLoad={() => setEditorReady(true)}
              allow="camera; microphone"
            />
          </div>

          {/* 底部错误提示 */}
          {error && (
            <div className="bg-red-50 border-t border-red-200 px-4 py-2 flex items-center gap-2 shrink-0">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700 flex-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
